import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  LinkSlashIcon,
  TagIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import GoLoginJobProgressBar from '../../../components/GoLoginJobProgressBar';
import { VpsAgentPicker, VpsPageHeader, fmt, useVpsAgents } from './vpsShared';

function gologinJobStorageKey(agentId) {
  return `wingr.vps.gologinJob.${agentId}`;
}

function readStoredGologinJob(agentId) {
  if (!agentId) return '';
  try {
    return localStorage.getItem(gologinJobStorageKey(agentId)) || '';
  } catch {
    return '';
  }
}

function writeStoredGologinJob(agentId, jobId) {
  if (!agentId) return;
  try {
    if (jobId) localStorage.setItem(gologinJobStorageKey(agentId), String(jobId));
    else localStorage.removeItem(gologinJobStorageKey(agentId));
  } catch { /* ignore */ }
}

function statusClass(status) {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'captcha':
    case 'auth_expired':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'suspended':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

export default function VpsAccounts() {
  const { user } = useAuth();
  const { agents, selectedId, selectAgent, reload: reloadAgents } = useVpsAgents();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [gologinBusy, setGologinBusy] = useState(false);
  const [gologinJobId, setGologinJobId] = useState(null);
  const [orbexImporting, setOrbexImporting] = useState(false);
  const [gologinJobProgress, setGologinJobProgress] = useState(null);
  const [gologinCancelling, setGologinCancelling] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [gologinFolders, setGologinFolders] = useState([]);
  const [gologinFoldersLoading, setGologinFoldersLoading] = useState(false);
  const [gologinFolderMode, setGologinFolderMode] = useState('existing');
  const [gologinFolderName, setGologinFolderName] = useState('');
  const [gologinImportSpeed, setGologinImportSpeed] = useState(1);
  /** null = show all; otherwise filter table to that VA profile status */
  const [statusFilter, setStatusFilter] = useState(null);
  const selectedIdRef = React.useRef(selectedId);
  selectedIdRef.current = selectedId;

  const load = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      setSelectedIds(new Set());
      return;
    }
    const agentId = selectedId;
    setLoading(true);
    setDetail(null);
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}`, {
        params: { accountsOnly: 1 }
      });
      if (agentId !== selectedIdRef.current) return;
      setDetail(res.data?.agent || null);
      setSelectedIds(new Set());
    } catch (err) {
      if (agentId === selectedIdRef.current) {
        toast.error(err.response?.data?.error || 'Failed to load accounts');
      }
    } finally {
      if (agentId === selectedIdRef.current) setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setStatusFilter(null);
  }, [selectedId]);

  // Resume GoLogin job progress after refresh / navigation.
  useEffect(() => {
    if (!selectedId) {
      setGologinJobId(null);
      setGologinJobProgress(null);
      setGologinBusy(false);
      setGologinCancelling(false);
      return;
    }
    const stored = readStoredGologinJob(selectedId);
    if (stored) {
      setGologinJobId(stored);
      setGologinBusy(true);
    } else {
      setGologinJobId(null);
      setGologinJobProgress(null);
      setGologinBusy(false);
      setGologinCancelling(false);
    }
  }, [selectedId]);

  const accounts = detail?.accounts || [];
  const disabled = useMemo(
    () => new Set((detail?.disabledAccountIds || []).map(String)),
    [detail?.disabledAccountIds]
  );
  const counts = accounts.reduce((acc, a) => {
    const st = a.status || 'active';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const filteredAccounts = useMemo(() => {
    if (!statusFilter) return accounts;
    return accounts.filter((a) => (a.status || 'active') === statusFilter);
  }, [accounts, statusFilter]);

  const pageIds = filteredAccounts.map((a) => String(a._id));
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const toggleStatusFilter = (status) => {
    setStatusFilter((prev) => (prev === status ? null : status));
    setSelectedIds(new Set());
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(pageIds));
  };

  const clearGologinJob = useCallback((agentId = selectedIdRef.current) => {
    writeStoredGologinJob(agentId, '');
    setGologinJobId(null);
    setGologinBusy(false);
    setGologinCancelling(false);
  }, []);

  const startGologinJobPolling = useCallback((jobId) => {
    if (!jobId) return;
    const agentId = selectedIdRef.current;
    writeStoredGologinJob(agentId, jobId);
    setGologinJobId(jobId);
    setGologinJobProgress(null);
    setGologinBusy(true);
    setGologinCancelling(false);
  }, []);

  const handleCancelGologinJob = async () => {
    if (!gologinJobId) return;
    // Second click (or already cancelling) force-finishes — needed after Railway
    // redeploy kills the in-process worker while Redis stays on "cancelling".
    const force = gologinCancelling || gologinJobProgress?.status === 'cancelling';
    setGologinCancelling(true);
    try {
      const res = await api.post(`/admin/va/gologin/jobs/${gologinJobId}/cancel`, { force });
      if (force || res.data?.status === 'cancelled' || res.data?.alreadyFinished) {
        toast('GoLogin job dismissed', { icon: '⏹' });
        clearGologinJob();
        await load();
      } else {
        toast('Stopping GoLogin job…', { icon: '⏹' });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop job');
      // Still let the operator dismiss a dead bar.
      if (force) {
        clearGologinJob();
      } else {
        setGologinCancelling(false);
      }
    }
  };

  const handleDismissGologinJob = async () => {
    if (!gologinJobId) {
      clearGologinJob();
      return;
    }
    setGologinCancelling(true);
    try {
      await api.post(`/admin/va/gologin/jobs/${gologinJobId}/cancel`, { force: true });
    } catch { /* ignore — clear local bar anyway */ }
    toast('Cleared stuck GoLogin progress', { icon: '⏹' });
    clearGologinJob();
    await load();
  };

  useEffect(() => {
    if (!gologinJobId) return undefined;

    let cancelled = false;
    let consecutiveErrors = 0;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await api.get(`/admin/va/gologin/jobs/${gologinJobId}`);
          if (cancelled) break;
          consecutiveErrors = 0;
          const job = res.data;
          setGologinJobProgress(job);
          if (job.status === 'cancelling') setGologinCancelling(true);

          if (job.status === 'completed' || job.status === 'cancelled') {
            const result = job.result || {};
            if (job.status === 'cancelled') {
              if (result.orphaned || result.forced) {
                toast(
                  `GoLogin stopped after deploy/restart — ${result.imported ?? result.processed ?? 0} done`
                  + (result.remaining ? `, ${result.remaining} left` : ''),
                  { icon: '⏹' }
                );
              } else {
                toast(
                  `GoLogin stopped — ${result.removed || result.imported || 0} done`
                  + (result.skipped ? `, ${result.skipped} skipped` : '')
                  + (result.failed ? `, ${result.failed} failed` : '')
                  + (result.remaining ? `, ${result.remaining} left` : ''),
                  { icon: '⏹' }
                );
              }
            } else if (job.type === 'gologin-remove') {
              toast.success(
                `GoLogin: ${result.removed || 0} removed`
                + (result.skipped ? `, ${result.skipped} skipped` : '')
                + (result.failed ? `, ${result.failed} failed` : '')
              );
            } else if (job.type === 'orbex-import') {
              toast.success(
                `Orbex: ${result.imported || 0} profiles generated, ${result.failed || 0} failed`
              );
            } else if (job.type === 'import') {
              const cookieNote = result.cookiesFailed
                ? ` · ${result.cookiesSet || 0} with AUTH cookie, ${result.cookiesFailed} missing cookie`
                : '';
              toast.success(
                `GoLogin: ${result.imported || 0} imported, ${result.skipped || 0} skipped, ${result.failed || 0} failed${cookieNote}`
              );
            } else {
              toast.success('GoLogin job completed');
            }
            setSelectedIds(new Set());
            setShowImportModal(false);
            clearGologinJob();
            await load();
            break;
          }

          if (job.status === 'failed') {
            toast.error(job.error || 'GoLogin job failed');
            clearGologinJob();
            break;
          }
        } catch (err) {
          consecutiveErrors += 1;
          // Job may still be running — don't drop the bar on a blip.
          if (err.response?.status === 404) {
            if (!cancelled && consecutiveErrors >= 3) {
              toast.error('GoLogin job no longer found (server may have restarted)');
              clearGologinJob();
              break;
            }
          } else if (consecutiveErrors >= 5) {
            if (!cancelled) {
              toast.error(err.response?.data?.error || 'Failed to poll GoLogin job');
              clearGologinJob();
            }
            break;
          }
        }
        await new Promise((r) => setTimeout(r, 1200));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [gologinJobId, load, clearGologinJob]);

  const handleBulkDetach = async () => {
    if (!selectedId || selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (!window.confirm(
      `Detach ${selectedIds.size} account(s) from this agent?\n\n`
      + 'They leave this VPS agent but stay on Wingr (and /dashboard/accounts). '
      + 'GoLogin profiles are not deleted.'
    )) return;

    setBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/detach-accounts`, {
        accountIds: Array.from(selectedIds)
      });
      toast.success(`Detached ${res.data?.detached ?? selectedIds.size} account(s)`);
      setSelectedIds(new Set());
      await load();
      reloadAgents?.({ silent: true, force: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Detach failed');
    } finally {
      setBusy(false);
    }
  };

  const fetchGologinFolders = async () => {
    setGologinFoldersLoading(true);
    try {
      const res = await api.get('/admin/va/gologin/folders');
      const folders = Array.isArray(res.data?.folders) ? res.data.folders : [];
      setGologinFolders(folders);
      return folders;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load GoLogin folders');
      setGologinFolders([]);
      return [];
    } finally {
      setGologinFoldersLoading(false);
    }
  };

  const selectedMissingGologinIds = useMemo(
    () => accounts
      .filter((a) => selectedIds.has(String(a._id)) && !String(a.gologinProfileId || '').trim())
      .map((a) => String(a._id)),
    [accounts, selectedIds]
  );

  // Orbex: only show button if Orbex is installed on the selected agent
  const selectedAgent = agents.find((a) => String(a._id) === selectedId);
  const orbexAvailable = Boolean(selectedAgent?.runtime?.orbexInstalled);

  const selectedMissingOrbexIds = useMemo(
    () => accounts
      .filter((a) => {
        if (!selectedIds.has(String(a._id))) return false;
        // "missing" means no profile yet, OR profile is on a different agent
        const hasOrbexHere = String(a.orbexProfileId || '').trim()
          && String(a.orbexAgentId || '') === selectedId;
        return !hasOrbexHere;
      })
      .map((a) => String(a._id)),
    [accounts, selectedIds, selectedId]
  );

  const openImportModal = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (selectedMissingGologinIds.length === 0) {
      toast.error('None of the selected accounts are missing GoLogin — only “missing” can be imported');
      return;
    }
    const agentFolder = String(detail?.gologinFolder?.name || '').trim();
    setGologinFolderMode(agentFolder ? 'existing' : 'existing');
    setGologinFolderName(agentFolder);
    setShowImportModal(true);
    const folders = await fetchGologinFolders();
    if (!agentFolder && folders.length) {
      setGologinFolderName(folders[0].name || '');
    }
  };

  const handleGologinImport = async (e) => {
    e.preventDefault();
    const folderName = String(gologinFolderName || '').trim();
    if (!folderName) {
      toast.error('Enter or select a folder name');
      return;
    }
    if (selectedMissingGologinIds.length === 0) {
      toast.error('None of the selected accounts are missing GoLogin');
      return;
    }
    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-import', {
        ids: selectedMissingGologinIds,
        folderName,
        folderMode: gologinFolderMode,
        speed: gologinImportSpeed
      });
      const skipped = res.data?.skippedAlreadyImported || 0;
      if (skipped > 0) {
        toast(`${skipped} already had GoLogin — importing ${res.data?.total || selectedMissingGologinIds.length} missing`, {
          icon: 'ℹ'
        });
      }
      startGologinJobPolling(res.data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'GoLogin import failed');
    }
  };

  const handleGologinRemove = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    const withProfile = accounts.filter(
      (a) => selectedIds.has(String(a._id)) && a.gologinProfileId
    ).length;
    if (!window.confirm(
      `Remove ${selectedIds.size} account(s) from GoLogin`
      + (withProfile < selectedIds.size ? ` (${withProfile} have a profile id)` : '')
      + '?\n\nWingr VA attachment and /dashboard/accounts are kept.'
    )) return;

    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-remove', {
        ids: Array.from(selectedIds)
      });
      startGologinJobPolling(res.data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'GoLogin remove failed');
    }
  };

  const handleGologinClearTag = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (!window.confirm(
      `Clear GoLogin tag on ${selectedIds.size} selected account(s)?\n\n`
      + 'Wingr will treat them as not imported (you can import again). '
      + 'This does NOT delete browser profiles in GoLogin — remove those manually if needed.'
    )) return;

    setBusy(true);
    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-clear-import', {
        ids: Array.from(selectedIds)
      });
      const modified = res.data?.modified ?? 0;
      if (modified === 0) {
        toast.error('No selected accounts had a GoLogin tag');
      } else {
        toast.success(`Cleared GoLogin tag on ${modified} account(s)`);
      }
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear GoLogin tag');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Unpark selected rows (captcha / auth_expired / suspended → active) so the
   * desktop agent can pick them again after auth_token reinject. Optionally
   * clear GoLogin tags so a fresh import is allowed after cloud delete.
   */
  const handleMarkActive = async ({ clearGologinTag = false } = {}) => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    const ids = Array.from(selectedIds);
    const parked = filteredAccounts.filter(
      (a) => selectedIds.has(String(a._id))
        && ['captcha', 'auth_expired', 'suspended'].includes(a.status || '')
    );
    const label = clearGologinTag
      ? `Mark ${ids.length} account(s) active and clear GoLogin tags?`
      : `Mark ${ids.length} account(s) as active?`;
    const detailMsg = clearGologinTag
      ? '\n\nUse this after GoLogin profiles were deleted — Wingr tags clear so you can Import to GoLogin again. '
        + 'Status returns to active (no longer auto-skipped as captcha).'
      : parked.length
        ? `\n\n${parked.length} selected are currently parked (captcha / auth_expired / suspended).`
        : '';
    if (!window.confirm(label + detailMsg)) return;

    setBusy(true);
    try {
      const res = await api.patch('/admin/va/profile-accounts/bulk-status', {
        ids,
        status: 'active'
      });
      let tagMsg = '';
      if (clearGologinTag) {
        const tagRes = await api.post('/admin/va/profile-accounts/gologin-clear-import', { ids });
        const cleared = tagRes.data?.modified ?? 0;
        tagMsg = cleared ? ` · cleared GoLogin tag on ${cleared}` : '';
      }
      toast.success(`Marked ${res.data?.modified ?? ids.length} active${tagMsg}`);
      setSelectedIds(new Set());
      await load();
      await reloadAgents();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark active');
    } finally {
      setBusy(false);
    }
  };

  const handleOrbexImport = async () => {
    if (selectedMissingOrbexIds.length === 0) {
      toast.error('None of the selected accounts are missing an Orbex profile');
      return;
    }
    if (!orbexAvailable) {
      toast.error('Orbex is not installed on this VPS — install it first via Orbex Licenses');
      return;
    }
    if (!window.confirm(
      `Generate Orbex profiles for ${selectedMissingOrbexIds.length} account(s) on ${selectedAgent?.name || 'this VPS'}?\n\n`
      + 'Each profile gets a full fingerprint (UA, WebGL, Canvas, Audio, Timezone) and will be written to the VPS disk. '
      + 'This replaces the GoLogin profile for Orbex runs — GoLogin profiles are not affected.'
    )) return;

    setOrbexImporting(true);
    try {
      const res = await api.post('/admin/va/profile-accounts/orbex-import', {
        ids: selectedMissingOrbexIds,
        agentId: selectedId,
      });
      const skipped = res.data?.skippedAlreadyImported || 0;
      const total = res.data?.total || 0;
      const skippedRows = Array.isArray(res.data?.skippedDuplicates) ? res.data.skippedDuplicates : [];
      if (skipped > 0) {
        const names = skippedRows.map((r) => r.username).filter(Boolean).slice(0, 8).join(', ');
        toast(
          total
            ? `${skipped} already on this VPS — importing ${total} missing${names ? ` (${names})` : ''}`
            : `${skipped} already on this VPS — nothing to import${names ? ` (${names})` : ''}`,
          { icon: 'ℹ' }
        );
      }
      if (res.data?.jobId) {
        startGologinJobPolling(res.data.jobId);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Orbex import failed');
    } finally {
      setOrbexImporting(false);
    }
  };

  const folderOptions = useMemo(() => {
    const current = gologinFolderName.trim();
    if (!current) return gologinFolders;
    const inList = gologinFolders.some((f) => f.name === current);
    if (inList) return gologinFolders;
    return [{ id: '__saved', name: current }, ...gologinFolders];
  }, [gologinFolders, gologinFolderName]);

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  const actionsDisabled = busy || gologinBusy || selectedIds.size === 0;

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="VPS Accounts"
        subtitle="Profiles attached to the selected AI VA agent. Detach leaves /dashboard/accounts intact."
        actions={(
          <>
            <VpsAgentPicker
              agents={agents}
              selectedId={selectedId}
              onSelect={selectAgent}
              onChanged={async (id) => {
                await reloadAgents({ force: true, silent: true });
                if (id) selectAgent(id);
              }}
            />
            <Link
              to="/dashboard/admin/vas"
              className="btn-secondary !py-1.5 !px-3 text-xs"
            >
              Manage in AI VA
            </Link>
          </>
        )}
      />

      <div className="flex flex-wrap gap-2 items-center">
        {['active', 'captcha', 'suspended', 'auth_expired'].map((k) => {
          const selected = statusFilter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleStatusFilter(k)}
              className={
                `px-3 py-1 rounded-full text-xs font-medium transition ring-offset-1 `
                + `${statusClass(k)} `
                + (selected
                  ? 'ring-2 ring-gray-900 dark:ring-white'
                  : 'opacity-80 hover:opacity-100')
              }
              title={selected ? `Showing ${k} only — click again to clear` : `Filter to ${k}`}
              aria-pressed={selected}
            >
              {k} · {fmt(counts[k] || 0)}
            </button>
          );
        })}
        {statusFilter && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter(null);
              setSelectedIds(new Set());
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
              bg-gray-100 text-gray-700 hover:bg-gray-200
              dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            Clear filter
          </button>
        )}
        {statusFilter && (
          <span className="text-xs text-gray-500">
            Showing {fmt(filteredAccounts.length)} of {fmt(accounts.length)}
          </span>
        )}
      </div>

      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
          <span className="text-xs text-gray-500 mr-1">
            {selectedIds.size} selected
            {selectedIds.size > 0 && (
              <span className="text-gray-400">
                {' '}· {selectedMissingGologinIds.length} missing GoLogin
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={handleBulkDetach}
            disabled={actionsDisabled}
            className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-40"
            title="Unassign from this agent — keeps Wingr + /dashboard/accounts"
          >
            <LinkSlashIcon className="w-3.5 h-3.5" />
            Detach
          </button>
          <button
            type="button"
            onClick={openImportModal}
            disabled={busy || gologinBusy || selectedMissingGologinIds.length === 0}
            className="inline-flex items-center gap-1.5 btn-primary !py-1.5 !px-3 text-xs disabled:opacity-40"
            title={
              selectedMissingGologinIds.length === 0
                ? 'Select accounts with GoLogin “missing” to import'
                : `Import ${selectedMissingGologinIds.length} account(s) missing GoLogin (ignores already-imported)`
            }
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            Import to GoLogin
            {selectedMissingGologinIds.length > 0 ? ` (${selectedMissingGologinIds.length})` : ''}
          </button>
          {/* Orbex import — only shown when Orbex is installed on the selected VPS */}
          {orbexAvailable ? (
            <button
              type="button"
              onClick={handleOrbexImport}
              disabled={busy || gologinBusy || orbexImporting || selectedMissingOrbexIds.length === 0}
              className="inline-flex items-center gap-1.5 !py-1.5 !px-3 text-xs font-medium rounded-lg border transition-colors disabled:opacity-40
                border-orange-400 text-orange-600 hover:bg-orange-50 dark:border-orange-500 dark:text-orange-400 dark:hover:bg-orange-900/20"
              title={
                selectedMissingOrbexIds.length === 0
                  ? 'All selected accounts already have an Orbex profile on this VPS'
                  : `Generate Orbex profiles for ${selectedMissingOrbexIds.length} account(s)`
              }
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              {orbexImporting ? 'Importing…' : 'Import to Orbex'}
              {selectedMissingOrbexIds.length > 0 ? ` (${selectedMissingOrbexIds.length})` : ''}
            </button>
          ) : selectedId ? (
            <span
              className="inline-flex items-center gap-1.5 !py-1.5 !px-3 text-xs font-medium rounded-lg border
                border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed opacity-50"
              title="Orbex is not installed on this VPS — install it via Orbex Licenses"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              Import to Orbex (not installed)
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleGologinRemove}
            disabled={actionsDisabled}
            className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-40"
            title="Delete GoLogin browser profiles only"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            Remove from GoLogin
          </button>
          <button
            type="button"
            onClick={handleGologinClearTag}
            disabled={actionsDisabled}
            className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-40"
            title="Clear Wingr GoLogin tag only — does not delete profiles in GoLogin"
          >
            <TagIcon className="w-3.5 h-3.5" />
            Clear GoLogin tag
          </button>
          {(statusFilter === 'captcha'
            || statusFilter === 'auth_expired'
            || statusFilter === 'suspended') && (
            <>
              <button
                type="button"
                onClick={() => handleMarkActive({ clearGologinTag: false })}
                disabled={actionsDisabled}
                className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-40"
                title="Set status to active and re-enable for runs"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Clear {statusFilter} → active
              </button>
              <button
                type="button"
                onClick={() => handleMarkActive({ clearGologinTag: true })}
                disabled={actionsDisabled}
                className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-40"
                title="Mark active + clear GoLogin tags so you can Import again after cloud delete"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Reset + clear GoLogin tag
              </button>
            </>
          )}
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={busy || gologinBusy}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      {gologinJobProgress && gologinBusy && (
        <div className="space-y-2">
          <GoLoginJobProgressBar
            progress={gologinJobProgress}
            title={
              gologinJobProgress.status === 'cancelling'
                ? 'Stopping…'
                : gologinJobProgress.type === 'orbex-import'
                  ? 'Importing to Orbex…'
                  : gologinJobProgress.type === 'gologin-remove'
                    ? 'Removing from GoLogin…'
                    : `Importing to GoLogin${gologinJobProgress.speed > 1 ? ` (${gologinJobProgress.speed}×)` : ''}…`
            }
          />
          <button
            type="button"
            onClick={handleCancelGologinJob}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400"
          >
            {gologinCancelling || gologinJobProgress.status === 'cancelling'
              ? 'Force stop'
              : 'Stop'}
          </button>
          {(gologinCancelling || gologinJobProgress.status === 'cancelling') && (
            <button
              type="button"
              onClick={handleDismissGologinJob}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
              title="Clear stuck progress after a Railway redeploy killed the worker"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={!filteredAccounts.length || busy || gologinBusy}
                    className="rounded border-gray-300"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-2 text-left font-medium">Username</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">In run</th>
                <th className="px-4 py-2 text-left font-medium">GoLogin</th>
                <th className="px-4 py-2 text-left font-medium">Orbex</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredAccounts.map((a) => {
                const id = String(a._id);
                const unselectable = ['captcha', 'suspended', 'auth_expired'].includes(a.status);
                const inRun = !disabled.has(id) && !unselectable;
                return (
                  <tr key={id} className={!inRun ? 'opacity-70' : ''}>
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(id)}
                        onChange={() => toggleOne(id)}
                        disabled={busy || gologinBusy}
                        className="rounded border-gray-300"
                        aria-label={`Select @${a.username}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 font-mono">@{a.username}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass(a.status)}`}>
                        {a.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {inRun ? 'selected' : unselectable ? 'auto-skipped' : 'deselected'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {a.gologinProfileId ? 'yes' : 'missing'}
                      {!a.gologinCookieSet && a.gologinProfileId ? ' · no cookies' : ''}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {(() => {
                        const hasOrbexHere = String(a.orbexProfileId || '').trim()
                          && String(a.orbexAgentId || '') === selectedId;
                        if (hasOrbexHere) {
                          return <span className="text-orange-500 font-medium">yes</span>;
                        }
                        if (a.orbexProfileId) {
                          return <span className="text-gray-400" title="Profile exists but on a different VPS">other VPS</span>;
                        }
                        return <span className="text-gray-400">missing</span>;
                      })()}
                    </td>
                  </tr>
                );
              })}
              {!filteredAccounts.length && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    {!selectedId
                      ? 'Select an agent above.'
                      : statusFilter
                        ? `No ${statusFilter} accounts.`
                        : 'No accounts attached to this agent.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !gologinBusy && setShowImportModal(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Import to GoLogin
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedMissingGologinIds.length} missing GoLogin
                  {selectedIds.size > selectedMissingGologinIds.length
                    ? ` (${selectedIds.size - selectedMissingGologinIds.length} already imported skipped)`
                    : ''}
                  {' '}→ folder on the GoLogin cloud account used by this agent.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !gologinBusy && setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleGologinImport} className="space-y-4">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    checked={gologinFolderMode === 'existing'}
                    onChange={() => setGologinFolderMode('existing')}
                    disabled={gologinBusy}
                  />
                  Use existing folder
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    checked={gologinFolderMode === 'new'}
                    onChange={() => setGologinFolderMode('new')}
                    disabled={gologinBusy}
                  />
                  Create new folder
                </label>
              </div>

              {gologinFolderMode === 'existing' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Folder
                  </label>
                  {gologinFoldersLoading ? (
                    <p className="text-xs text-gray-500">Loading folders…</p>
                  ) : (
                    <select
                      value={gologinFolderName}
                      onChange={(e) => setGologinFolderName(e.target.value)}
                      className="form-select w-full text-sm"
                      disabled={gologinBusy || folderOptions.length === 0}
                    >
                      {folderOptions.length === 0 ? (
                        <option value="">No folders — create a new one</option>
                      ) : (
                        folderOptions.map((folder) => (
                          <option key={folder.id || folder.name} value={folder.name}>
                            {folder.name}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={fetchGologinFolders}
                    disabled={gologinFoldersLoading || gologinBusy}
                    className="mt-2 text-xs text-indigo-600 hover:underline disabled:opacity-50"
                  >
                    Refresh folder list
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New folder name
                  </label>
                  <input
                    type="text"
                    value={gologinFolderName}
                    onChange={(e) => setGologinFolderName(e.target.value)}
                    className="form-input w-full text-sm"
                    placeholder="e.g. Ryzen-1"
                    maxLength={80}
                    disabled={gologinBusy}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Speed
                </label>
                <select
                  value={gologinImportSpeed}
                  onChange={(e) => setGologinImportSpeed(Number(e.target.value) || 1)}
                  className="form-select w-full text-sm"
                  disabled={gologinBusy}
                >
                  <option value={1}>1× normal</option>
                  <option value={2}>2×</option>
                  <option value={3}>3×</option>
                  <option value={5}>5×</option>
                  <option value={10}>10×</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Higher speeds shorten delays between GoLogin uploads. 5×–10× may hit rate limits on large batches.
                </p>
              </div>

              {detail?.gologinFolder?.name && (
                <p className="text-[11px] text-gray-500">
                  Agent auto-import folder: <span className="font-medium">{detail.gologinFolder.name}</span>
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  disabled={gologinBusy}
                  className="btn-secondary !py-1.5 !px-3 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={gologinBusy || !gologinFolderName.trim()}
                  className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-40"
                >
                  {gologinBusy ? 'Importing…' : 'Start import'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
